import { get } from 'lodash-es';
import queryString from 'query-string';

export const BOOKING_CHANNEL_WEB = 'web';

export const BOOKING_FLOWS = {
  BROWSE: 'browse',
  BOOK_AGAIN: 'book_again',
  REBOOK: 'rebook',
};

export const BOOKING_SCREENS = {
  APPOINTMENTS: 'Appointments',
  BLOG_DEEP_LINK: 'BlogDeepLink',
  BOOKED_CONFIRMATION: 'BookedConfirmation',
  HEADER_BOOK: 'HeaderBook',
  HOME_CTA: 'HomeCta',
  HOME_DEEP_LINK: 'HomeDeepLink',
  HOW_IT_WORKS: 'HowItWorks',
  MAIN_MENU: 'MainMenu',
  MEMBERSHIP_BANNER: 'MembershipBanner',
  MY_THERAPISTS: 'MyTherapists',
  PRICING: 'Pricing',
  PROFILE: 'Profile',
  THERAPIST_DEEP_LINK: 'TherapistDeepLink',
  THERAPIST_LIST: 'TherapistList',
  THERAPIST_PROFILE: 'TherapistProfile',
};

const NAMED_FLOWS = new Set(Object.values(BOOKING_FLOWS));
const BOOKING_SCREEN_MAX_LENGTH = 64;

const BOOKING_FUNNEL_PATH_REGEXES = [
  /^\/appointment\/locations(?:\/|$)/,
  /^\/appointment\/location\//,
  /^\/appointment\/therapist\//,
  /^\/appointment\/confirm/,
  /^\/appointment\/register/,
  /^\/appointment\/login/,
  /^\/therapist\/[^/]+\/info\/book/,
];

export function isBookingFunnelPath(pathname = '') {
  return BOOKING_FUNNEL_PATH_REGEXES.some(re => re.test(pathname || ''));
}

export function isTherapistBookPath(pathname = '') {
  return /^\/therapist\/[^/]+\/info\/book/.test(pathname || '');
}

export function sanitizeFlow(flow) {
  if (typeof flow !== 'string') return null;
  const normalized = flow.trim().toLowerCase();
  if (normalized === 'admin') return null;
  return NAMED_FLOWS.has(normalized) ? normalized : null;
}

export function sanitizeScreen(screen) {
  if (typeof screen !== 'string') return null;
  const trimmed = screen
    .replace(/[^A-Za-z0-9]/g, '')
    .slice(0, BOOKING_SCREEN_MAX_LENGTH);
  return trimmed || null;
}

export function hasBookingAttribution(attribution) {
  if (!attribution || typeof attribution !== 'object') return false;
  return Boolean(
    sanitizeFlow(attribution.flow) || sanitizeScreen(attribution.screen),
  );
}

export function compactBookingAttribution(attribution = {}) {
  const flow = sanitizeFlow(attribution.flow);
  const screen = sanitizeScreen(attribution.screen);
  const next = {};
  if (flow) next.flow = flow;
  if (screen) next.screen = screen;
  return next;
}

function attributionsEqual(a = {}, b = {}) {
  return (
    (a.flow || '') === (b.flow || '') && (a.screen || '') === (b.screen || '')
  );
}

function toPascalScreen(pathname = '') {
  return sanitizeScreen(
    pathname
      .split('/')
      .filter(Boolean)
      .map(seg => seg.replace(/[^A-Za-z0-9]/g, ''))
      .filter(Boolean)
      .map(seg => seg.charAt(0).toUpperCase() + seg.slice(1))
      .join(''),
  );
}

export function attributionFromSearch(search = '') {
  const query = queryString.parse(search || '');
  return compactBookingAttribution({
    flow: query.booking_flow,
    screen: query.booking_screen,
  });
}

export function withBookingAttributionQuery(url, attribution = {}) {
  if (!url) return url;
  const [path, existingSearch] = String(url).split('?');
  const params = queryString.parse(existingSearch || '');
  const compacted = compactBookingAttribution(attribution);
  if (compacted.flow) params.booking_flow = compacted.flow;
  if (compacted.screen) params.booking_screen = compacted.screen;
  const qs = queryString.stringify(params);
  return qs ? `${path}?${qs}` : path;
}

export function attributionFromPaths(currentPathname = '', lastPathname = '') {
  const last = lastPathname || '';
  const current = currentPathname || '';

  if (/^\/gratuity/.test(last)) {
    return { flow: BOOKING_FLOWS.REBOOK };
  }

  if (isTherapistBookPath(current)) {
    if (/^\/appointments(?:\/|$)/.test(last)) {
      return { flow: BOOKING_FLOWS.BOOK_AGAIN };
    }
    if (/^\/therapist\/list/.test(last)) {
      return {
        flow: BOOKING_FLOWS.BROWSE,
        screen: BOOKING_SCREENS.THERAPIST_LIST,
      };
    }
    if (/^\/therapist\/[^/]+\/info$/.test(last)) {
      return {
        flow: BOOKING_FLOWS.BROWSE,
        screen: BOOKING_SCREENS.THERAPIST_PROFILE,
      };
    }
    if (/^\/my-therapists/.test(last)) {
      return {
        flow: BOOKING_FLOWS.BROWSE,
        screen: BOOKING_SCREENS.MY_THERAPISTS,
      };
    }
    if (!last) {
      return {
        flow: BOOKING_FLOWS.BROWSE,
        screen: BOOKING_SCREENS.THERAPIST_DEEP_LINK,
      };
    }
    const screen = screenFromLastPath(last);
    return compactBookingAttribution({
      flow: BOOKING_FLOWS.BROWSE,
      screen,
    });
  }

  if (isBookingFunnelPath(last)) {
    return { flow: BOOKING_FLOWS.BROWSE };
  }

  if (!last) {
    return { flow: BOOKING_FLOWS.BROWSE };
  }

  if (last === '/') {
    if (/^\/appointment\/location\//.test(current)) {
      return {
        flow: BOOKING_FLOWS.BROWSE,
        screen: BOOKING_SCREENS.HOME_DEEP_LINK,
      };
    }
    return {
      flow: BOOKING_FLOWS.BROWSE,
      screen: BOOKING_SCREENS.HOME_CTA,
    };
  }

  const screen = screenFromLastPath(last);
  return compactBookingAttribution({
    flow: BOOKING_FLOWS.BROWSE,
    screen,
  });
}

function screenFromLastPath(lastPathname = '') {
  const last = lastPathname || '';
  if (
    /^\/blog(?:\/|$)/.test(last) ||
    /^\/best-massage/.test(last) ||
    /^\/massage-near-me/.test(last)
  ) {
    return BOOKING_SCREENS.BLOG_DEEP_LINK;
  }
  if (/^\/membership/.test(last)) {
    return BOOKING_SCREENS.MEMBERSHIP_BANNER;
  }
  if (/^\/pricing/.test(last)) {
    return BOOKING_SCREENS.PRICING;
  }
  if (/^\/appointments(?:\/|$)/.test(last)) {
    return BOOKING_SCREENS.APPOINTMENTS;
  }
  if (/^\/appointment\/booked/.test(last)) {
    return BOOKING_SCREENS.BOOKED_CONFIRMATION;
  }
  if (/^\/how-it-works/.test(last)) {
    return BOOKING_SCREENS.HOW_IT_WORKS;
  }
  if (/^\/profile/.test(last) || /^\/wallet/.test(last)) {
    return BOOKING_SCREENS.PROFILE;
  }
  if (/^\/therapist\/list/.test(last)) {
    return BOOKING_SCREENS.THERAPIST_LIST;
  }
  if (/^\/my-therapists/.test(last)) {
    return BOOKING_SCREENS.MY_THERAPISTS;
  }
  if (/^\/gift/.test(last)) {
    return 'Gift';
  }
  return toPascalScreen(last);
}

export function captureBookingAttribution({
  currentPathname = '',
  lastPathname = '',
  search = '',
  existingAttribution = {},
} = {}) {
  const existing = compactBookingAttribution(existingAttribution);
  const lastInFunnel = isBookingFunnelPath(lastPathname);

  if (lastInFunnel && hasBookingAttribution(existing)) {
    return existing;
  }

  const fromQuery = attributionFromSearch(search);
  if (hasBookingAttribution(fromQuery)) {
    return fromQuery;
  }

  if (!lastPathname && hasBookingAttribution(existing)) {
    return existing;
  }

  return attributionFromPaths(currentPathname, lastPathname);
}

export function applyCapturedBookingAttribution(props) {
  const setBookingAttribution = get(props, 'setBookingAttribution');
  if (typeof setBookingAttribution !== 'function') return null;

  const next = captureBookingAttribution({
    currentPathname: get(props, 'location.pathname', ''),
    search: get(props, 'location.search', ''),
    lastPathname: get(props, 'lastLocation.pathname', ''),
    existingAttribution: get(props, 'appointmentData.bookingAttribution'),
  });
  const existing = compactBookingAttribution(
    get(props, 'appointmentData.bookingAttribution'),
  );
  if (!hasBookingAttribution(next) || attributionsEqual(existing, next)) {
    return next;
  }
  setBookingAttribution(next);
  return next;
}

export function ensureBrowseBookingAttribution(props) {
  const setBookingAttribution = get(props, 'setBookingAttribution');
  if (typeof setBookingAttribution !== 'function') return null;
  const existing = compactBookingAttribution(
    get(props, 'appointmentData.bookingAttribution'),
  );
  if (existing.flow) return existing;
  // Therapist-first UI flow is used by book_again / rebook; do not infer browse.
  if (get(props, 'appointmentData.bookingFlow') === 'therapist') {
    return existing;
  }
  const next = compactBookingAttribution({
    ...existing,
    flow: BOOKING_FLOWS.BROWSE,
  });
  setBookingAttribution(next);
  return next;
}

export function buildConfirmAttributionPayload(attribution = {}) {
  const compacted = compactBookingAttribution(attribution);
  const payload = {
    booking_channel: BOOKING_CHANNEL_WEB,
  };
  if (compacted.flow) {
    payload.booking_flow = compacted.flow;
  }
  if (compacted.screen) {
    payload.booking_screen = compacted.screen;
  }
  return payload;
}
